<template>
  <div>
    <div class="nk-app-root">
      <div class="nk-main">
        <div class="nk-wrap">
          <Header v-show="!loading_page" />
          <div class="nk-content">
            <div class="container wide-xl">
              <div class="nk-content-inner">
                <Navigation v-show="!loading_page" />
                <div class="nk-content-body" v-show="!loading_page">
                  <div class="nk-content-wrap">
                    <div class="nk-block nk-block-lg">
                      <div class="nk-block-head">
                        <div class="nk-block-head-content d-flex justify-content-between align-items-center">
                          <h4 class="nk-block-title">{{ $t('dm_anlagen_title') }}</h4>
                          <div>
                            <button v-if="canDmAdmin" class="btn btn-sm btn-outline-primary mr-2" @click="showImportConfirm" :disabled="importing">
                              <em class="icon ni ni-download"></em>
                              {{ importing ? $t('dm_anlagen_btn_import_loading') : $t('dm_anlagen_btn_import') }}
                            </button>
                            <button class="btn btn-sm btn-outline-secondary mr-2" @click="toggleView">
                              <em :class="viewMode === 'list' ? 'icon ni ni-map-pin' : 'icon ni ni-list'"></em>
                              {{ viewMode === 'list' ? $t('dm_anlagen_view_map') : $t('dm_anlagen_view_list') }}
                            </button>
                            <button v-if="canDmEdit" class="btn btn-sm btn-primary" @click="createAnlage">
                              <em class="icon ni ni-plus"></em> {{ $t('dm_anlagen_btn_new') }}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="form-control-wrap mb-3" v-if="viewMode === 'list'">
                        <input type="text" class="form-control" :placeholder="$t('dm_common_quick_filter')" v-model="quickFilterText" />
                      </div>

                      <div v-if="viewMode === 'list'" class="card card-bordered">
                        <div class="dm-table-wrap">
                          <table class="dm-table">
                            <thead>
                              <tr>
                                <th v-for="col in tableColumns" :key="col.field" @click="toggleSort(col.field)" class="dm-table-th">
                                  {{ col.label }}
                                  <span v-if="sortField === col.field" class="dm-sort-icon">{{ sortAsc ? '&#9650;' : '&#9660;' }}</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="row in filteredData" :key="row._id" @click="onRowClicked(row)" class="dm-table-row">
                                <td v-for="col in tableColumns" :key="col.field">
                                  <span v-if="col.field === 'status_8p2.bestaetigt'" class="dm-status-dot" :class="resolveField(row, col.field) ? 'dm-status-confirmed' : 'dm-status-unconfirmed'" :title="resolveField(row, col.field) ? $t('dm_common_yes') : $t('dm_common_no')"></span>
                                  <template v-else>{{ getCellValue(row, col) }}</template>
                                </td>
                              </tr>
                              <tr v-if="filteredData.length === 0">
                                <td :colspan="tableColumns.length" class="text-center text-muted py-4">{{ $t('dm_common_no_entries_found') }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div v-if="viewMode === 'map'" ref="mapContainer" style="width: 100%; height: 500px;"></div>

                      <!-- Import Result -->
                      <div v-if="importResult" class="card mt-4">
                        <div class="card-inner">
                          <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6 class="title mb-0">{{ $t('dm_anlagen_import_result_title') }}</h6>
                            <button class="btn btn-sm btn-outline-secondary" @click="importResult = null">
                              <em class="icon ni ni-cross"></em>
                            </button>
                          </div>
                          <div class="row g-3 mb-3">
                            <div class="col-sm-3">
                              <div class="import-stat import-stat-success">
                                <span class="import-stat-value">{{ importResult.imported }}</span>
                                <span class="import-stat-label">{{ $t('dm_anlagen_import_imported') }}</span>
                              </div>
                            </div>
                            <div class="col-sm-3">
                              <div class="import-stat import-stat-muted">
                                <span class="import-stat-value">{{ importResult.skipped }}</span>
                                <span class="import-stat-label">{{ $t('dm_anlagen_import_skipped') }}</span>
                              </div>
                            </div>
                            <div class="col-sm-3">
                              <div class="import-stat import-stat-muted">
                                <span class="import-stat-value">{{ importResult.skipped_no_serial || 0 }}</span>
                                <span class="import-stat-label">{{ $t('dm_anlagen_import_no_serial') }}</span>
                              </div>
                            </div>
                            <div class="col-sm-3">
                              <div class="import-stat import-stat-info">
                                <span class="import-stat-value">{{ importResult.adressen_created || 0 }}</span>
                                <span class="import-stat-label">{{ $t('dm_anlagen_import_addresses_created') }}</span>
                              </div>
                            </div>
                          </div>
                          <div v-if="importResult.details && importResult.details.length > 0" style="max-height: 300px; overflow-y: auto;">
                            <table class="table table-sm">
                              <thead>
                                <tr>
                                  <th>{{ $t('dm_common_col_serial_number') }}</th>
                                  <th>{{ $t('dm_common_col_status') }}</th>
                                  <th>{{ $t('dm_common_col_details') }}</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(item, idx) in importResult.details" :key="idx">
                                  <td class="project-number">{{ item.seriennummer }}</td>
                                  <td>
                                    <span class="badge" :class="importStatusBadge(item.status)">{{ importStatusText(item.status) }}</span>
                                  </td>
                                  <td class="text-muted">{{ item.error || '' }}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <div v-if="importResult.imported > 0" class="mt-3">
                            <button class="btn btn-sm btn-primary" @click="filterUnconfirmed">
                              <em class="icon ni ni-filter"></em> {{ $t('dm_anlagen_btn_show_unconfirmed') }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Footer />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "../../../components/Header.vue";
import Navigation from "../../../components/Navigation.vue";
import Footer from "../../../components/Footer.vue";
import dmTableMixin from "@/mixins/dm-table-mixin";
import dmPermissionsMixin from "@/mixins/dm-permissions-mixin";
import { getAnlagenList, importInspectScan } from "@/services/dm.service";

export default {
  name: "DmAnlagen",
  components: { Header, Navigation, Footer },
  mixins: [dmTableMixin, dmPermissionsMixin],
  data() {
    return {
      fullLoader: null,
      anlagen: [],
      viewMode: "list",
      importing: false,
      importResult: null,
      map: null,
      markers: [],
    };
  },
  computed: {
    tableColumns() {
      return [
        { label: this.$t('dm_common_col_serial_number'), field: "seriennummer" },
        { label: this.$t('dm_common_col_confirmed'), field: "status_8p2.bestaetigt", formatter: function(val) { return val ? "Ja" : "Nein"; } },
        { label: this.$t('dm_common_col_manufacturer'), field: "anlagendaten.hersteller" },
        { label: this.$t('dm_common_col_type'), field: "typ" },
        { label: this.$t('dm_common_col_type_name'), field: "anlagendaten.typ_bezeichnung" },
        { label: this.$t('dm_common_col_power_kw'), field: "anlagendaten.leistung_kw" },
        { label: this.$t('dm_common_col_location'), field: "standort.name" },
        { label: this.$t('dm_common_col_contractors'), field: "contractors", formatter: function(val) { return Array.isArray(val) && val.length > 0 ? val.join(", ") : "–"; } },
        { label: this.$t('dm_common_col_inspections'), field: "pruefungen", formatter: function(val) { return Array.isArray(val) ? val.length : 0; } },
      ];
    },
    loading_page() {
      return this.$store.state.loading_page && this.$store.state.loading_page == "setup";
    },
    filteredData() {
      return this.filterAndSort(this.anlagen, this.tableColumns);
    },
  },
  created() {
    this.loadAnlagen();
  },
  methods: {
    loadAnlagen() {
      this.showLoader();
      var filters = {};
      if (this.$route.query.ampel) {
        filters.ampel = this.$route.query.ampel;
      }
      if (this.$route.query.bestaetigt === "false") {
        filters.bestaetigt = false;
      }
      getAnlagenList(filters)
        .then((d) => {
          this.hideLoader();
          if (d.data && d.data.list) {
            this.anlagen = d.data.list;
            this.$store.commit("set_dm_anlagen", d.data.list);
          }
        })
        .catch((e) => {
          this.hideLoader();
          this.$store.dispatch("api_error", e);
        });
    },
    onRowClicked(row) {
      if (row && row._id) {
        this.$router.push("/dm/anlage/" + row._id);
      }
    },
    createAnlage() {
      this.$router.push("/dm/anlage/new");
    },
    showImportConfirm() {
      this.$swal({
        title: this.$t('dm_anlagen_import_title'),
        text: this.$t('dm_anlagen_import_description'),
        icon: "info",
        showCancelButton: true,
        confirmButtonText: this.$t('dm_anlagen_btn_import_start'),
        cancelButtonText: this.$t('dm_common_cancel'),
      }).then((result) => {
        if (result.value) {
          this.runImport();
        }
      });
    },
    runImport() {
      this.importing = true;
      this.importResult = null;
      importInspectScan()
        .then((d) => {
          this.importing = false;
          var err = this.getApiError(d); if (err) {
            this.$swal(this.$t('dm_common_error'), err, "error");
            return;
          }
          this.importResult = d.data || {};
          this.loadAnlagen();
        })
        .catch((e) => {
          this.importing = false;
          this.$store.dispatch("api_error", e);
        });
    },
    filterUnconfirmed() {
      this.importResult = null;
      this.$router.push({ path: "/dm/anlagen", query: { bestaetigt: "false" } });
      this.loadAnlagen();
    },
    importStatusBadge(status) {
      if (status === "imported") return "badge-success";
      if (status === "skipped_exists") return "badge-secondary";
      if (status === "error") return "badge-danger";
      return "badge-secondary";
    },
    importStatusText(status) {
      if (status === "imported") return this.$t('dm_anlagen_import_status_imported');
      if (status === "skipped_exists") return this.$t('dm_anlagen_import_status_exists');
      if (status === "error") return this.$t('dm_common_error');
      return status;
    },
    toggleView() {
      this.viewMode = this.viewMode === "list" ? "map" : "list";
      if (this.viewMode === "map") {
        this.$nextTick(() => { this.initMap(); });
      }
    },
    initMap() {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      /* global L */
      this.map = L.map(this.$refs.mapContainer).setView([51.5, 10.0], 6);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(this.map);

      this.markers = [];
      this.anlagen.forEach((a) => {
        var coords = a.standort && a.standort.geokoordinaten && a.standort.geokoordinaten.coordinates;
        if (coords && coords[0] && coords[1]) {
          var lat = coords[1];
          var lng = coords[0];
          var label = (a.seriennummer || "") + "<br>" + (a.anlagendaten && a.anlagendaten.hersteller || "") + " " + (a.typ || "");
          var marker = L.marker([lat, lng])
            .addTo(this.map)
            .bindPopup("<b>" + label + "</b>");
          marker.on("click", () => {
            this.$router.push("/dm/anlage/" + a._id);
          });
          this.markers.push(marker);
        }
      });

      if (this.markers.length > 0) {
        var group = L.featureGroup(this.markers);
        this.map.fitBounds(group.getBounds().pad(0.1));
      }
    },
  },
};
</script>

<style scoped>
.table-row { cursor: pointer; }
.table-row:hover { background-color: #f5f5f5; }

.import-stat {
  text-align: center;
  padding: 16px 12px;
  border-radius: 8px;
  background: var(--bg-page, #F5F5F3);
}

.import-stat-success {
  background: #F0FDF4;
}

.import-stat-info {
  background: rgba(232, 117, 26, 0.08);
}

.import-stat-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary, #1A1A1A);
}

.import-stat-label {
  font-size: 12px;
  color: var(--text-muted, #8C8C8C);
  font-weight: 500;
}

.project-number {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-weight: 600;
  font-size: 13px;
}

.badge-success { background: #24c45f; color: #fff; }
.badge-secondary { background: #E5E5E3; color: #555; }
.badge-danger { background: #EF4444; color: #fff; }

.dm-table-wrap {
  max-height: calc(100vh - 220px);
  overflow: auto;
}

.dm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  table-layout: fixed;
}

.dm-table th,
.dm-table td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dm-table thead {
  background: #f8f9fa;
  border-bottom: 2px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.dm-table-th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.dm-table-th:hover {
  color: #334155;
}

.dm-sort-icon {
  font-size: 10px;
  margin-left: 4px;
  color: #E8751A;
}

.dm-table tbody td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.dm-table-row {
  cursor: pointer;
  transition: background 0.15s;
}

.dm-table-row:hover {
  background: #f8fafc;
}

.dm-status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dm-status-confirmed {
  background-color: #24c45f;
}

.dm-status-unconfirmed {
  background-color: #E5E5E3;
}
</style>
